import { Link, useParams } from "react-router-dom";
import { Check, MapPin, ArrowRight, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, Overline } from "../components/Motion";
import { Process, EstimateSection, WhyUs } from "../components/Sections";
import { Reviews } from "../components/Social";
import { PageHero } from "./ServicePage";
import { Seo, BUSINESS_ID, faqLd, crumbsLd } from "../components/Seo";
import { BIZ, SITE, SERVICES, STORM } from "../data/site";
import { TOWNS, townBySlug, KEYWORDS } from "../data/areas";
import Home from "./Home";

const townFaqs = (t, s) => {
  const what = s ? s.title.toLowerCase() : "roofing, siding and gutters";
  return [
    { q: `Do you offer ${what} in ${t.name}, WI?`, a: `Yes. ${BIZ.name} works on homes all over ${t.name} and the rest of ${t.county} County. Call ${BIZ.phone} or request a free quote online.` },
    { q: `How much does ${s ? (s.slug === "roofing" ? "a new roof" : s === STORM ? "storm damage repair" : `new ${what}`) : "the work"} cost in ${t.name}?`, a: "Every home is different, so we start with a free inspection, including drone photos of your roof, and then give you a clear written estimate with your options explained." },
    ...(s === STORM ? [] : [{ q: `Can you help with hail or storm damage in ${t.name}?`, a: `Yes. We inspect the damage, document it with photos and guide you through your insurance claim step by step. See our ${t.name} storm damage page for details.` }]),
    ...(s ? s.faqs : []),
  ];
};

const Offerings = ({ s, t }) => (
  <section className="bg-white py-20 md:py-28">
    <div className="wrap grid items-start gap-14 lg:grid-cols-12">
      <Reveal className="lg:col-span-6">
        <Overline>{t.name} {KEYWORDS[s.slug].pro}</Overline>
        <h2 className="font-display text-4xl font-black uppercase leading-[0.9] text-slate-900 md:text-6xl">Looking for a {KEYWORDS[s.slug].pro} near you in {t.name}?</h2>
        <p className="mt-6 text-slate-600">{s.intro}</p>
        <p className="mt-4 text-slate-600">{t.name} homeowners{t.busy && s.slug === "roofing" ? " are some of our most frequent roofing customers. They" : ""} call {BIZ.name} for {KEYWORDS[s.slug].terms.slice(1, 7).join(", ")} and more. We are a local, GAF Certified™, BBB A-rated {KEYWORDS[s.slug].proLong} based in Milwaukee, serving ZIP codes {t.zips.join(", ")}.</p>
      </Reveal>
      <ul className="grid gap-3 sm:grid-cols-2 lg:col-span-6">
        {s.offerings.map((o, i) => (
          <li key={o} className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 font-semibold text-slate-800" data-testid={`area-offering-${i}`}>
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-slate-900"><Check className="h-4 w-4 text-white" /></span>{o} in {t.name}
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const ServiceCards = ({ t, skip }) => (
  <section className="bg-white py-20 md:py-28" data-testid="area-services">
    <div className="wrap">
      <Reveal><Overline>Our services in {t.name}</Overline>
        <h2 className="font-display text-4xl font-black uppercase leading-[0.9] text-slate-900 md:text-6xl">{skip ? `More for ${t.name} homes` : `Roofing, siding & gutters in ${t.name}`}</h2></Reveal>
      <div className="mt-10 grid gap-5 md:grid-cols-3">
        {SERVICES.filter((s) => s.slug !== skip).map((s) => (
          <Link key={s.slug} to={`/${s.slug}/${t.slug}`} className="group relative block h-64 overflow-hidden rounded-2xl" data-testid={`area-service-${s.slug}`}>
            <img loading="lazy" decoding="async" src={s.img} alt={`${s.title} project near ${t.name}, WI`} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-slate-950/60" />
            <div className="relative flex h-full flex-col justify-end p-6 text-white">
              <span className="font-display text-3xl font-black uppercase">{s.title} in {t.name}</span>
              <span className="mt-2 flex items-center gap-2 text-sm text-slate-200">{s.blurb} <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" /></span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const Faqs = ({ faqs, t }) => (
  <section className="bg-slate-50 py-20 md:py-28" data-testid="area-faq">
    <div className="wrap grid gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-4"><Overline>Questions</Overline><h2 className="font-display text-4xl font-black uppercase leading-[0.9] text-slate-900 md:text-5xl">{t.name} FAQ</h2></Reveal>
      <Accordion type="single" collapsible className="lg:col-span-8">
        {faqs.map((f, i) => (
          <AccordionItem key={f.q} value={`q${i}`} className="border-slate-300">
            <AccordionTrigger className="font-display py-6 text-left text-xl font-bold uppercase text-slate-900 hover:no-underline" data-testid={`area-faq-trigger-${i}`}>{f.q}</AccordionTrigger>
            <AccordionContent className="text-base text-slate-600">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

const Nearby = ({ t, s }) => (
  <section className="bg-white py-16" data-testid="area-nearby">
    <div className="wrap">
      <h2 className="font-display text-3xl font-black uppercase text-slate-900">{s ? `${s.title} near ${t.name}` : `Also serving near ${t.name}`}</h2>
      <div className="mt-6 flex flex-wrap gap-2">
        {t.near.map(townBySlug).map((n) => (
          <Link key={n.slug} to={s ? `/${s.slug}/${n.slug}` : `/service-areas/${n.slug}`} className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 transition-colors hover:border-slate-900" data-testid={`nearby-${n.slug}`}>
            <MapPin className="h-3.5 w-3.5" />{s ? `${s.title} in ${n.name}, WI` : `${n.name}, WI`}
          </Link>
        ))}
        <Link to="/service-areas" className="rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white" data-testid="nearby-all">All service areas</Link>
      </div>
    </div>
  </section>
);

const StormStrip = ({ t }) => (
  <section className="bg-white pb-16">
    <div className="wrap">
    <Link to={`/storm-damage/${t.slug}`} className="group flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-slate-950 p-6 text-white" data-testid="area-storm-link">
      <span><span className="font-display block text-2xl font-black uppercase">Hail or storm damage in {t.name}?</span><span className="text-sm text-slate-300">Free inspection with drone photos and help with your insurance claim.</span></span>
      <span className="flex items-center gap-2 font-semibold">Storm damage repair <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
    </Link>
    </div>
  </section>
);

const TITLES = { roofing: "Roofer", siding: "Siding Contractor", gutters: "Gutter Installation", "storm-damage": "Hail Damage Roof Repair" };

export default function AreaPage({ service }) {
  const { town } = useParams();
  const t = townBySlug(town);
  if (!t) return <Home />;
  const s = [...SERVICES, STORM].find((x) => x.slug === service);
  const path = s ? `/${s.slug}/${t.slug}` : `/service-areas/${t.slug}`;
  const faqs = townFaqs(t, s);
  const title = s
    ? `${TITLES[s.slug]} in ${t.name}, WI | Good Home Improvement`
    : `Roofing, Siding & Gutters in ${t.name}, WI | Good Home Improvement`;
  const description = s
    ? `${t.name}, WI ${KEYWORDS[s.slug].proLong}: ${KEYWORDS[s.slug].terms.slice(1, 4).join(", ")}. GAF Certified, BBB A rated. Free estimate: ${BIZ.phone}.`
    : `Local roofer, siding contractor and gutter installer in ${t.name}, WI. Roof replacement, storm damage, seamless gutters. Free estimate: ${BIZ.phone}.`;
  const crumbs = [["Home", "/"], ["Service areas", "/service-areas"], [`${t.name}, WI`, `/service-areas/${t.slug}`], ...(s ? [[s.title, path]] : [])];
  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "Service", name: s ? `${s.title} in ${t.name}, WI` : `Roofing, siding and gutters in ${t.name}, WI`, serviceType: s ? s.kicker : "Roofing, siding and gutter installation", url: `${SITE}${path}`,
        areaServed: { "@type": "City", name: `${t.name}, Wisconsin` }, provider: { "@id": BUSINESS_ID } },
      faqLd(faqs), crumbsLd(crumbs),
    ],
  };
  return (
    <main data-testid={s ? `area-${s.slug}-page` : "area-page"}>
      <Seo title={title} description={description} path={path} jsonld={jsonld} />
      <PageHero img={s ? s.img : SERVICES[0].img} crumb={s ? `${s.title} · ${t.name}` : `${t.name}, WI`}
        kicker={s ? `${KEYWORDS[s.slug].terms[0]} · ${t.name}` : `Serving ${t.name} & ${t.county} County`}
        lines={s ? [`${s.h1 ?? s.title} in`, <span key="t" className="text-amber-400">{t.name}, WI</span>] : ["Roofing, siding", "& gutters in", <span key="t" className="text-amber-400">{t.name}, WI</span>]}
        sub={`${BIZ.name} is a GAF Certified™, BBB A-rated ${s ? KEYWORDS[s.slug].proLong : "roofing, siding and gutter contractor"} serving ${t.name}. Free estimates and drone inspections.`}
        testid="area-hero" />
      {s ? <Offerings s={s} t={t} /> : null}
      <ServiceCards t={t} skip={s?.slug} />
      {s !== STORM && <StormStrip t={t} />}
      <WhyUs img={s ? s.why : SERVICES[0].why} />
      <Reviews />
      <Process />
      <Faqs faqs={faqs} t={t} />
      <Nearby t={t} s={s} />
      <section className="bg-white pb-16">
        <div className="wrap flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-slate-100 p-6">
          <p className="font-semibold text-slate-900">Need a {s ? KEYWORDS[s.slug].pro : "roofer"} in {t.name} today?</p>
          <a href={BIZ.tel} className="flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 font-semibold text-white" data-testid="area-call-button"><Phone className="h-4 w-4" /> Call {BIZ.phone}</a>
        </div>
      </section>
      <EstimateSection service={s?.formService ?? s?.title} title={[s ? `${t.name} ${s.title.toLowerCase()}` : `${t.name} homes`, "estimate free"]} />
    </main>
  );
}
